import React from 'react'
import { Fab } from '@material-ui/core'
import AddIcon from '@material-ui/icons/Add'
import { computeSpokeOffsets } from './radialMath'
import './radialFab.css'

const RADIUS = 140
const STAGGER_MS = 40

/**
 * Shared radial quick-actions FAB.
 * Props:
 *   actions  - [{ key, label, icon, onClick }]  (required)
 *   arcSpan  - degrees of the arc, centered on the up-left diagonal (default 120)
 *   color    - MUI Fab color (default 'primary')
 *   position - reserved; only 'bottom-right' is implemented (default)
 */
const RadialFab = ({ actions = [], arcSpan = 120, color = 'primary' }) => {
  const [open, setOpen] = React.useState(false)

  // Escape closes the menu
  React.useEffect(() => {
    if (!open) return undefined
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false) }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open])

  if (!actions.length) return null

  const offsets = computeSpokeOffsets(actions.length, arcSpan, RADIUS)

  const handleSpokeClick = (action) => {
    setOpen(false)            // close first — a throwing onClick can't leave it stuck open
    if (action.onClick) action.onClick()
  }

  return (
    <>
      <div
        className={`radial-fab-scrim${open ? ' is-open' : ''}`}
        onClick={() => setOpen(false)}
      />
      <div className={`radial-fab-root${open ? ' is-open' : ''}`}>
        {actions.map((action, i) => {
          const { x, y } = offsets[i]
          // Stagger: opening fans 0..N; closing collapses N..0 (last leaves first)
          const delay = open ? i * STAGGER_MS : (actions.length - 1 - i) * STAGGER_MS
          return (
            <div
              key={action.key}
              className="radial-fab-spoke"
              style={{
                '--spoke-x': `${x}px`,
                '--spoke-y': `${y}px`,
                transitionDelay: `${delay}ms`
              }}
            >
              <Fab
                size="small"
                aria-label={action.label}
                onClick={() => handleSpokeClick(action)}
              >
                {action.icon}
              </Fab>
              <span className="radial-fab-label">{action.label}</span>
            </div>
          )
        })}
        <Fab
          color={color}
          aria-label={open ? 'Close quick actions' : 'Open quick actions'}
          onClick={() => setOpen(o => !o)}
        >
          <AddIcon className="radial-fab-trigger-icon" />
        </Fab>
      </div>
    </>
  )
}

export default RadialFab
